import { useMemo } from 'react';
import { array, number, object, Schema } from 'yup';
import {
	IAncillaryGroupSelection,
	IAncillarySelection,
	IExtraSelection,
} from '../../../../domain/ancillary/domain/models/IAncillaryGroupSelection';
import { IExtrasFormData } from '../IExtrasFormData';

export function useExtrasSchema() {
	return useMemo(
		() =>
			object<IExtrasFormData>({
				ancillaryGroups: ancillaryGroupsSelectionSchema,
			}).required(),
		[],
	);
}

/**
 * Either a valid number or null
 */
const idSchema = number()
	.transform((value) => (Number.isNaN(value) ? null : value))
	.defined();

/**
 * Converts a single value into an array
 * - Using `ensure` a single value is converted an array with a single item
 * - Using `compact` any null values are removed from the array
 * - Using `default` it will cast input to an empty array by default
 */
const ensureArray = <T>(schema: Schema<T>) =>
	array().of(schema).ensure().compact().default([]).defined();

/**
 * An array of IDs
 */
const idListSchema = ensureArray(idSchema);

/**
 * A selection of Extras:
 * - A selection contains an array of objects
 * - Every object has an Extra ID (not nullable)
 * - Every object has a selection of Extra Variants
 */
const extrasSelectionSchema = ensureArray(
	object<IExtraSelection>({
		id: idSchema.required(),
		selected: idListSchema,
	}).required(),
);

/**
 * A selection of Ancillaries:
 * - A selection contains an array of objects
 * - Every object has an Ancillary ID (not nullable)
 * - Every object has of Extras with sub-selections (also ancillaries without extras will have an empty array)
 * - Using `ensure` a single object is converted an array of objects
 * - Using `compact` any null values are removed from the array
 */
const ancillariesSelectionSchema = ensureArray(
	object<IAncillarySelection>({
		id: idSchema.required(),
		extras: extrasSelectionSchema,
	})
		.transform((value, originalValue) =>
			// Transform a single Ancillary ID to an Ancillary object with an empty extras array
			typeof originalValue === 'string' && originalValue !== ''
				? { id: Number(originalValue), extras: [] }
				: value,
		)
		// NB: object or `null` (no ancillary selected is allowed)
		.defined(),
);

/**
 * A selection of Ancillary Groups:
 * - A selection contains an array of objects
 * - Every object is required to have an Ancillary Group ID (not nullable)
 * - Every object can have a selection of Ancillaries
 * - Using `ensure` a single object is converted an array of objects
 * - Using `compact` any null values are removed from the array
 */
const ancillaryGroupsSelectionSchema = ensureArray(
	object<IAncillaryGroupSelection>({
		id: idSchema.required(),
		selected: idListSchema,
		ancillaries: ancillariesSelectionSchema,
	}).required(),
);
